import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { useBooth } from "../BoothContext";
import { resolveAssetUrl } from "@/components/ui";

/**
 * The payoff screen — and a handoff, not a till.
 *
 * ## Why there is nothing to tap here
 *
 * Payment used to happen on this screen: priced buttons, a phone-number sheet,
 * a USSD push, a poll. All of it correct, and all of it in the worst possible
 * place. A guest paying at the kiosk occupies the booth for thirty to sixty
 * seconds while a queue watches. The booth's throughput became "how fast can
 * people find their phone and remember their PIN".
 *
 * So the transaction moves to the guest's own phone. This screen does one
 * thing: show them the photo they just took, and a code that carries the whole
 * rest of the experience — pricing, payment, download, print — onto a device
 * they're already holding. They step aside, the next guest steps up, and
 * everything slow happens in parallel.
 *
 * ## The QR is always shown
 *
 * It used to appear only once sharing was unlocked, because it pointed at a
 * page that would refuse to serve an unpaid photo. That is exactly backwards
 * now: the QR *is* how you pay. Hiding it until payment means the guest can
 * never start. `/s/:token` always loads and gates the file rather than the page.
 *
 * The photo shown here is full-size and clean, which is safe on a kiosk — there
 * is no way to save an image off a screen you can't long-press. The phone gets
 * a downscaled preview until it's paid for, which is a different problem solved
 * in `SharePage`.
 */

/** Kiosks get abandoned mid-screen. Returning to the attract loop keeps the queue moving. */
const AUTO_ADVANCE_MS = 45_000;

export function ResultScreen() {
  const booth = useBooth();
  const [qr, setQr] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const imageUrl = resolveAssetUrl(booth.resultUrl ?? undefined);

  const shareUrl = booth.shareToken
    ? `${window.location.origin}/s/${booth.shareToken}`
    : null;

  useEffect(() => {
    if (!shareUrl) { setQr(null); return; }

    void QRCode.toDataURL(shareUrl, {
      // Generous: this is scanned across a metre of booth, sometimes at an
      // angle, often by an older phone camera.
      width: 640,
      margin: 2,
      errorCorrectionLevel: "M",
      color: { dark: "#000000", light: "#ffffff" },
    }).then(setQr).catch(() => setQr(null));
  }, [shareUrl]);

  useEffect(() => setImageFailed(false), [imageUrl]);

  // Nobody presses Done. Left alone, this screen would hold the booth open with
  // the previous guest's face on it.
  useEffect(() => {
    const timer = window.setTimeout(() => booth.go("completion"), AUTO_ADVANCE_MS);
    return () => window.clearTimeout(timer);
  }, [booth]);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 overflow-y-auto px-6 py-14">
      <h2
        className="text-center text-2xl font-bold uppercase tracking-tight"
        style={{ color: "var(--booth-headline)" }}
      >
        {booth.say("result_title", "Here's your photo")}
      </h2>

      <div className="flex flex-wrap items-center justify-center gap-8">
        {imageUrl && !imageFailed && (
          <img
            src={imageUrl}
            alt="Your photo"
            onError={() => setImageFailed(true)}
            className="max-h-[46vh] w-auto rounded-2xl shadow-2xl"
          />
        )}

        {imageFailed && <p role="alert" className="max-w-md rounded-xl bg-red-950/70 px-5 py-4 text-center text-sm text-white">The saved photo could not be loaded. Please retry the export.</p>}

        {/* Hardcoded white/black below, on purpose. A QR has to be dark-on-light
            to scan reliably, so this panel must NOT follow the booth theme —
            theming it is how you get a code nobody's camera can read. */}
        {qr && (
          <figure className="flex flex-col items-center gap-3 rounded-3xl bg-white p-5 shadow-2xl">
            {/* Large on purpose. This is the only interactive element on the
                screen and the entire flow depends on it scanning first time. */}
            <img src={qr} alt="Scan to keep your photo" className="h-52 w-52" />
            <figcaption className="max-w-[13rem] text-center">
              <span className="block text-sm font-bold uppercase tracking-wide text-black">
                {booth.config.event.qr_code?.label ?? "Scan to keep your photo"}
              </span>
              <span className="mt-1 block text-xs leading-relaxed text-black/55">
                Download, print or share it from your own phone.
              </span>
            </figcaption>
          </figure>
        )}
      </div>

      {!qr && (
        <p className="max-w-md rounded-xl bg-booth-surface-strong px-4 py-3 text-center text-sm text-booth-ink">
          Your photo is ready, but the share code couldn't be drawn. Ask a member
          of staff — they can find it in the gallery.
        </p>
      )}

      <button
        onClick={() => imageFailed ? booth.go("vibes") : booth.go("completion")}
        className="rounded-xl px-12 py-4 text-base font-semibold uppercase tracking-wide"
        style={{ background: "var(--booth-secondary)", color: "var(--booth-cta-text)" }}
      >
        {imageFailed ? "Retry" : "Done"}
      </button>

      <p className="text-center text-xs uppercase tracking-wider text-booth-ink-muted">
        Scan before you go — this screen clears shortly
      </p>
    </div>
  );
}
